import { getCalculatorDefinition } from "@/calculators/registry";
import { NAV_KEYS } from "@/navigation/keys";

export type CalculatorsLocation = {
  calculatorId?: string;
  faClientId?: string;
};

export const CALCULATORS_OS_PARAM_KEYS = [
  NAV_KEYS.calculatorId,
  NAV_KEYS.faClientId,
] as const;

export function normalizeCalculatorsLocation(
  loc: CalculatorsLocation,
): CalculatorsLocation {
  const calculatorId = loc.calculatorId?.trim() || undefined;
  const faClientId = loc.faClientId?.trim() || undefined;
  return {
    ...(calculatorId ? { calculatorId } : {}),
    ...(faClientId ? { faClientId } : {}),
  };
}

export function parseCalculatorsFromSearchParams(
  searchParams: URLSearchParams,
): CalculatorsLocation {
  return normalizeCalculatorsLocation({
    calculatorId: searchParams.get(NAV_KEYS.calculatorId) ?? undefined,
    faClientId: searchParams.get(NAV_KEYS.faClientId) ?? undefined,
  });
}

export function parseCalculatorsFromOsParams(
  params: Record<string, string>,
): CalculatorsLocation {
  return normalizeCalculatorsLocation({
    calculatorId: params[NAV_KEYS.calculatorId] ?? undefined,
    faClientId: params[NAV_KEYS.faClientId] ?? undefined,
  });
}

export function serializeCalculatorsToSearchParams(
  loc: CalculatorsLocation,
): URLSearchParams {
  const normalized = normalizeCalculatorsLocation(loc);
  const out = new URLSearchParams();
  if (normalized.faClientId) {
    out.set(NAV_KEYS.faClientId, normalized.faClientId);
  }
  if (normalized.calculatorId && !normalized.calculatorId.includes("/")) {
    out.set(NAV_KEYS.calculatorId, normalized.calculatorId);
  }
  return out;
}

export function serializeCalculatorsToOsParams(
  loc: CalculatorsLocation,
): Record<string, string> {
  const params = serializeCalculatorsToSearchParams(loc);
  const out: Record<string, string> = {};
  params.forEach((value, key) => {
    out[key] = value;
  });
  return out;
}

/** Shareable path (no origin) for classic router and OS navigate. */
export function buildCalculatorsPath(loc: CalculatorsLocation = {}): string {
  const normalized = normalizeCalculatorsLocation(loc);
  const qs = serializeCalculatorsToSearchParams({
    faClientId: normalized.faClientId,
  }).toString();
  const suffix = qs ? `?${qs}` : "";
  if (normalized.calculatorId) {
    return `/calculators/${encodeURIComponent(normalized.calculatorId)}${suffix}`;
  }
  return `/calculators${suffix}`;
}

export function buildCalculatorsWindowUrl(loc: CalculatorsLocation = {}): string {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return `${origin}${buildCalculatorsPath(loc)}`;
}

export function calculatorsWindowTitle(loc: CalculatorsLocation): string {
  const def = getCalculatorDefinition(loc.calculatorId);
  if (def) return def.label;
  return "Calculators";
}

/** OS `openWindow` payload for a calculators deep link. */
export function buildCalculatorsOpenWindowConfig(loc: CalculatorsLocation = {}) {
  const normalized = normalizeCalculatorsLocation(loc);
  const params = serializeCalculatorsToOsParams({
    faClientId: normalized.faClientId,
  });
  if (normalized.calculatorId) {
    params[NAV_KEYS.calculatorId] = normalized.calculatorId;
  }
  return {
    appId: "calculators" as const,
    title: calculatorsWindowTitle(normalized),
    params,
  };
}
